import { ConnectionDetails, QueryInputType, convertConnectionToDetails, mergeConnectionDetails as merge } from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DispatchIssueWithCache,
  DispatchIssueWithCacheSearch,
  DispatchIssueWithCacheTaxiUsedEnum,
  SearchDispatchIssueWithCacheResponseDispatchIssueWithCacheConnectionArgs,
  SortDirectionEnum,
} from '@/models/gen/graphql';
import {
  createDispatchIssueDocumentDownloadUrl,
  recreateDispatchIssueDocumentDownloadUrl,
} from '@/features/IssueHistory/api/services/createDispatchIssueDocumentDownloadURL';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import { getProperty, queryInput } from '@/utils';
import { refetchDispatchIssueDocument, searchDispatchIssueDocument } from './searchDispatchIssueDocument';

import { DispatchIssueWithDocuments } from '../../components/IssueHistoryForm/types';
import { SearchDispatchIssue } from '@/features/IssueHistory/api/queries/SearchDispatchIssue';
import { TypedDocumentNode } from '@apollo/client';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  airportCode?: string[];
  tripServicerIataAirlineCodes?: string[];
  taxiUsed?: DispatchIssueWithCacheTaxiUsedEnum;
} & { [key in keyof DispatchIssueWithCacheSearch]?: any })[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<DispatchIssueWithCache>>;
}

interface ServiceMethodWithDocuments {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<DispatchIssueWithDocuments>>;
}

// SearchDispatchIssue Service
export const { searchDispatchIssue, refetchDispatchIssue, useDispatchIssue } = ((): {
  searchDispatchIssue: ServiceMethod;
  refetchDispatchIssue: ServiceMethod;
  useDispatchIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchDispatchIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<DispatchIssueWithCache>> => {
        const query: DispatchIssueWithCacheSearch[] = payload?.map((item) => ({
          tripId: queryInput.orNull(item?.id),
          createdAt:
            item?.startDatetime && item?.endDatetime
              ? queryInput.date([item?.startDatetime, item?.endDatetime], false, SortDirectionEnum.Desc)
              : item?.id // If we are searching by trip id this is a history table, and we can get all the dispatch issues and sort by datetime
                ? queryInput([], QueryInputType.ISNOTNULL, SortDirectionEnum.Desc)
                : null,
          station: queryInput.orNull(item?.airportCode),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
          taxiUsed: queryInput(item?.taxiUsed),
        }));
        const args: SearchDispatchIssueWithCacheResponseDispatchIssueWithCacheConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query,
          },
        };
        const opts = { merge: options?.merge ? 'searchDispatchIssue.dispatchIssueTableConnection' : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty('searchDispatchIssue.dispatchIssueTableConnection', res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchDispatchIssue: fetch,
    refetchDispatchIssue: refetch,
    useDispatchIssue: useService,
  };
})();
export const { searchDispatchIssueWithDocuments, refetchDispatchIssueWithDocuments } = ((): {
  searchDispatchIssueWithDocuments: ServiceMethodWithDocuments;
  refetchDispatchIssueWithDocuments: ServiceMethodWithDocuments;
} => {
  const fetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<DispatchIssueWithDocuments>> => {
    const DispatchIssues = await searchDispatchIssue(payload, options);
    const res: ConnectionDetails<DispatchIssueWithDocuments> = {
      ...DispatchIssues,
      rows: DispatchIssues.rows.map((row) => ({
        ...row,
        documents: [],
        causedByForm: {},
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await searchDispatchIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await createDispatchIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };

  const refetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<DispatchIssueWithDocuments>> => {
    const DispatchIssues = await refetchDispatchIssue(payload, options);
    const res: ConnectionDetails<DispatchIssueWithDocuments> = {
      ...DispatchIssues,
      rows: DispatchIssues.rows.map((row) => ({
        ...row,
        documents: [],
        causedByForm: Array.isArray(row.causedBy)
          ? row.causedBy.reduce<Record<string, string>>((acc, item) => {
              if (item && item.id && item.causedById) {
                acc[item.id] = item.causedById;
              }
              return acc;
            }, {})
          : {},
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await refetchDispatchIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await recreateDispatchIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };
  return {
    searchDispatchIssueWithDocuments: fetch,
    refetchDispatchIssueWithDocuments: refetch,
  };
})();
